import { addDays, format, parseISO } from 'date-fns';
import type { MetricSnapshot } from '@/types/analyticsV2';
import type {
  DurationMetric,
  MetricRate,
  SentinelAnalyticsReport,
  SentinelAnalyticsSeriesPoint,
  SentinelMessageMetrics,
  SentinelPerformanceRow,
  SentinelTicketMetrics,
  SentinelTurnBucket,
} from '@/types/sentinelAnalytics';
import type {
  SentinelBreakdownRow,
  SentinelMetricKey,
  SentinelMetrics,
  SentinelSeries,
  SentinelSeriesKey,
} from './metric-keys';

/** One metric's numbers over a set of tickets: a card's current period, or a table row. */
export type Snapshot = (key: SentinelMetricKey) => MetricSnapshot;

const EMPTY: MetricSnapshot = { count: 0, total: 0, percent: null };

/** A breakdown row's metrics; a metric the row has no tickets for reads as empty. */
export const rowSnapshot = (row: SentinelBreakdownRow): Snapshot => (key) => row.metrics[key] ?? EMPTY;

/** The exact rate from its counts; kim-analytics' percent is already rounded to two decimals. */
function rate({ count, total }: MetricSnapshot): MetricRate {
  return { count, total, rate: total === 0 ? null : count / total };
}

/** An average arrives as the ratio in `percent`, over `total` samples. */
const durationStats = ({ total, percent }: MetricSnapshot) => (percent === null ? null : { averageMs: percent, sampleSize: total });

/** Business hours have no samples when the brand has none configured. */
const duration = (calendar: MetricSnapshot, business: MetricSnapshot): DurationMetric => ({ calendar: durationStats(calendar), business: durationStats(business) });

/** Business-hours durations exist only when the brand (or account) has business hours set. */
export const hasBusinessTime = (get: Snapshot) => get('sentinelFirstResponseBusinessTime').total > 0 || get('sentinelResolutionBusinessTime').total > 0;

export function toTicketMetrics(get: Snapshot): SentinelTicketMetrics {
  return {
    totalTickets: get('sentinelTotalTickets').count,
    inScopeTickets: get('sentinelInScope').count,
    outOfScopeTickets: get('sentinelOutOfScope').count,
    inScopeShare: rate(get('sentinelInScope')),
    unassignment: {
      ...rate(get('sentinelUnassignment')),
      causes: { sentinelReject: get('sentinelUnassignedBySentinel').count, humanTookOver: get('sentinelUnassignedByHuman').count },
    },
    missedOpportunity: rate(get('sentinelMissedOpportunity')),
    handled: rate(get('sentinelHandled')),
    automation: rate(get('sentinelAutomation')),
    reopen: rate(get('sentinelReopenRate')),
    ftnr: rate(get('sentinelFtnr')),
    csat: { average: get('sentinelCsat').percent, ratings: get('sentinelCsat').total },
    csatResponse: rate(get('sentinelCsatResponse')),
    firstResponse: duration(get('sentinelFirstResponseTime'), get('sentinelFirstResponseBusinessTime')),
    resolution: duration(get('sentinelResolutionTime'), get('sentinelResolutionBusinessTime')),
  };
}

export function toMessageMetrics(get: Snapshot, failureReasons: SentinelMessageMetrics['aiGenerationFailure']['reasons'] = []): SentinelMessageMetrics {
  const accurate = get('sentinelAiAccuracy');
  const corrected = get('sentinelCorrectedResponses');
  return {
    aiCoverage: rate(get('sentinelAiCoverage')),
    aiGenerationFailure: { ...rate(get('sentinelAiGenerationFailure')), reasons: failureReasons },
    gradedDrafts: accurate.total,
    accurateResponses: rate(accurate),
    correctedResponses: rate(corrected),
    effort: { human: corrected.count, ai: accurate.count },
    oversightPrompts: {
      oneToTwo: get('sentinelOversightPromptsOneToTwo').count,
      moreThanTwo: get('sentinelOversightPromptsMoreThanTwo').count,
    },
  };
}

/** One Performance table bucket; the Automation row's accuracy keeps its automated replies, which no one reviewed. */
export function toPerformanceRow(bucket: SentinelTurnBucket, get: Snapshot): SentinelPerformanceRow {
  return {
    bucket,
    tickets: get('sentinelHandled').count,
    aiAccuracy: rate(get(bucket === 'automation' ? 'sentinelAiAccuracyWithAutomated' : 'sentinelAiAccuracy')),
    csat: { average: get('sentinelCsat').percent, ratings: get('sentinelCsat').total },
    reopen: rate(get('sentinelReopenRate')),
    ftnr: rate(get('sentinelFtnr')),
    firstResponse: duration(get('sentinelFirstResponseTime'), get('sentinelFirstResponseBusinessTime')),
    resolution: duration(get('sentinelResolutionTime'), get('sentinelResolutionBusinessTime')),
    oversightPrompts: {
      oneToTwo: get('sentinelOversightPromptsOneToTwo').count,
      moreThanTwo: get('sentinelOversightPromptsMoreThanTwo').count,
    },
  };
}

/** The page's report from kim-analytics' Sentinel cards, daily series and failure reasons. */
export function toSentinelReport(
  metrics: SentinelMetrics,
  series: SentinelSeries,
  failureReasons: SentinelBreakdownRow[],
  fromDate: string,
  toDate: string,
): SentinelAnalyticsReport {
  const get: Snapshot = (key) => metrics[key]?.current ?? EMPTY;
  const reasons = failureReasons.flatMap(({ dims, metrics: reasonMetrics }) =>
    dims.failureReason ? [{ reason: dims.failureReason, count: reasonMetrics.sentinelAiGenerationFailure?.count ?? 0 }] : [],
  );
  return {
    tickets: toTicketMetrics(get),
    messages: toMessageMetrics(get, reasons),
    series: dailySeries(series, fromDate, toDate),
    businessTime: { available: hasBusinessTime(get) },
  };
}

/** One point per day in the range, oldest first; a day with no rows keeps its place with zero counts. */
function dailySeries(series: SentinelSeries, fromDate: string, toDate: string): SentinelAnalyticsSeriesPoint[] {
  const index = new Map(Object.entries(series).flatMap(([key, { buckets }]) => buckets.map((b) => [`${key}:${b.date}`, b] as const)));
  const bucket = (key: SentinelSeriesKey, date: string) => index.get(`${key}:${date}`);
  const count = (key: SentinelSeriesKey, date: string) => bucket(key, date)?.count ?? 0;
  const total = (key: SentinelSeriesKey, date: string) => bucket(key, date)?.total ?? 0;
  const average = (key: SentinelSeriesKey, date: string) => bucket(key, date)?.percent ?? null;

  const points: SentinelAnalyticsSeriesPoint[] = [];
  for (let day = parseISO(fromDate); format(day, 'yyyy-MM-dd') <= toDate; day = addDays(day, 1)) {
    const date = format(day, 'yyyy-MM-dd');
    points.push({
      date,
      totalTickets: count('sentinelTotalTickets', date),
      inScopeTickets: count('sentinelInScope', date),
      outOfScopeTickets: count('sentinelOutOfScope', date),
      missedOpportunityTickets: count('sentinelMissedOpportunity', date),
      handledTickets: count('sentinelHandled', date),
      automatedTickets: count('sentinelAutomation', date),
      unassignedTickets: count('sentinelUnassignment', date),
      resolvedTickets: total('sentinelReopenRate', date),
      reopenedTickets: count('sentinelReopenRate', date),
      regrettableReopenedTickets: count('sentinelFtnr', date),
      turns: total('sentinelAiCoverage', date),
      coveredTurns: count('sentinelAiCoverage', date),
      failedTurns: count('sentinelAiGenerationFailure', date),
      gradedDrafts: total('sentinelAiAccuracy', date),
      accurateDrafts: count('sentinelAiAccuracy', date),
      correctedDrafts: count('sentinelCorrectedResponses', date),
      firstResponseAverageMs: average('sentinelFirstResponseTime', date),
      resolutionAverageMs: average('sentinelResolutionTime', date),
      firstResponseBusinessAverageMs: average('sentinelFirstResponseBusinessTime', date),
      resolutionBusinessAverageMs: average('sentinelResolutionBusinessTime', date),
    });
  }
  return points;
}
